import api, { API } from "@/lib/api";
import { formatEuro } from "@/lib/resources";
import { toast } from "sonner";

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const num = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString("es-ES", { maximumFractionDigits: 2 }) : "";
};

export async function printPresupuesto(projectId) {
  // Abrir la ventana ANTES del await para no perder el gesto del usuario (evita bloqueo de popups).
  const w = window.open("", "_blank");
  if (!w) {
    toast.error("Permite las ventanas emergentes para imprimir.");
    return;
  }
  w.document.write('<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Presupuesto</title></head><body style="font-family:Georgia,serif;color:#1a3a5c;padding:48px;font-size:15px">Generando presupuesto…</body></html>');

  let data;
  try {
    const res = await api.get(`/projects/${projectId}/presupuesto`);
    data = res.data;
  } catch (e) {
    try { w.document.body.innerHTML = "No se pudo generar el presupuesto."; } catch (_) {}
    toast.error("No se pudo generar el presupuesto.");
    return;
  }

  const { project, company = {}, levels, totals_by_type, grand_total } = data;
  const today = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  const origin = window.location.origin;
  const coverTag = project.cover_path
    ? `<img class="cover" src="${API}/files/${esc(project.cover_path)}" onerror="this.style.display='none'"/>`
    : "";
  const logoSrc = company.logo_path ? `${API}/files/${esc(company.logo_path)}` : `${origin}/mori-logo.jpg`;
  const companyLine = [company.name, company.cif_nif ? ("CIF/NIF: " + company.cif_nif) : "", company.address].filter(Boolean).map(esc).join(" · ");
  const companyContact = [company.phone, company.email, company.website].filter(Boolean).map(esc).join(" · ");
  const companyBlock = (companyLine || companyContact)
    ? `<div class="company">${companyLine ? `<div class="cl">${companyLine}</div>` : ""}${companyContact ? `<div class="cc">${companyContact}</div>` : ""}</div>`
    : "";

  const activityBlock = (a) => {
    const resRows = (a.resources || [])
      .map(
        (r) => `<tr class="res">
          <td class="rname">${esc(r.name)}</td>
          <td class="uds">${num(r.calculated_units)} ${esc(r.unit)}</td>
          <td class="eur">${formatEuro(r.subtotal_interna)}</td>
          <td class="eur venta">${formatEuro(r.subtotal_venta)}</td>
        </tr>`
      )
      .join("");
    return `<tbody class="act">
      <tr class="act-head">
        <td class="aname"><strong>${esc(a.name)}</strong><span class="meta">${esc(a.type)}${a.area_name ? " · " + esc(a.area_name) : ""}${a.related_units != null ? " · " + num(a.related_units) + " " + esc(a.related_unit) : ""}</span></td>
        <td></td>
        <td class="eur">${formatEuro(a.subtotal_interna)}</td>
        <td class="eur venta">${formatEuro(a.subtotal_venta)}</td>
      </tr>
      ${resRows || '<tr class="res empty"><td colspan="4">Sin recursos asignados</td></tr>'}
    </tbody>`;
  };

  const levelBlocks = levels
    .map(
      (l) => `<table class="grid">
        <thead>
          <tr class="lvl"><th colspan="2">${esc(l.name)}</th><th class="eur">Compra int.</th><th class="eur">Venta</th></tr>
        </thead>
        ${l.activities.map(activityBlock).join("")}
        <tfoot>
          <tr class="lvl-total"><td colspan="2">Total ${esc(l.name)}</td><td class="eur">${formatEuro(l.subtotal_interna)}</td><td class="eur venta">${formatEuro(l.subtotal_venta)}</td></tr>
        </tfoot>
      </table>`
    )
    .join("");

  const typeRows = (totals_by_type || [])
    .map(
      (t) => `<tr><td>${esc(t.type)}</td><td class="eur">${formatEuro(t.subtotal_interna)}</td><td class="eur venta">${formatEuro(t.subtotal_venta)}</td></tr>`
    )
    .join("");

  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"/>
  <title>Presupuesto · ${esc(project.name)}</title>
  <style>
    @page { size: A4 portrait; margin: 14mm 12mm; }
    * { box-sizing: border-box; }
    body { font-family: Georgia, 'Times New Roman', serif; color: #1b2a3a; margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .topbar { display:flex; align-items:center; justify-content:space-between; border-bottom:3px solid #1a3a5c; padding-bottom:8px; margin-bottom:12px; }
    .topbar .logo { height:34px; }
    .topbar .doc { font-size:12px; letter-spacing:.14em; text-transform:uppercase; color:#C75B28; font-weight:bold; }
    .cover { width:100%; height:200px; object-fit:cover; border-radius:8px; margin-bottom:14px; }
    h1 { font-size:26px; margin:0 0 2px; color:#1a3a5c; }
    .loc { color:#5a6b7b; font-size:13px; margin:0 0 4px; }
    .date { color:#8a97a3; font-size:11px; margin:0 0 18px; }
    table.grid { width:100%; border-collapse:collapse; margin-bottom:16px; page-break-inside:auto; }
    table.grid th, table.grid td { padding:5px 8px; font-size:12px; vertical-align:top; }
    tr.lvl th { background:#1a3a5c; color:#fff; text-align:left; font-size:12px; letter-spacing:.03em; }
    tr.lvl th.eur { text-align:right; width:88px; font-weight:normal; }
    tr.act-head td { border-top:1px solid #d7dee5; font-size:12.5px; }
    tr.act-head .aname { padding-top:8px; }
    .meta { display:block; font-size:10.5px; color:#6b7886; font-weight:normal; margin-top:1px; }
    tr.res td { color:#44525f; font-size:11px; padding-top:2px; padding-bottom:2px; }
    tr.res .rname { padding-left:16px; }
    tr.res .rname:before { content:'— '; color:#C75B28; }
    tr.res.empty td { color:#9aa6b1; font-style:italic; padding-left:16px; }
    td.uds { white-space:nowrap; color:#6b7886; }
    td.eur, th.eur { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
    td.eur.venta { color:#C75B28; font-weight:bold; }
    tr.lvl-total td { border-top:2px solid #1a3a5c; font-weight:bold; background:#f3f6f9; }
    .summary { margin-top:10px; page-break-inside:avoid; }
    .summary h2 { font-size:13px; color:#1a3a5c; margin:0 0 6px; border-bottom:1px solid #d7dee5; padding-bottom:4px; }
    table.types { width:100%; border-collapse:collapse; }
    table.types td { padding:4px 8px; font-size:12px; border-bottom:1px solid #eef2f5; }
    .grand { margin-top:14px; background:#C75B28; color:#fff; border-radius:8px; padding:12px 16px; display:flex; align-items:center; justify-content:space-between; gap:12px; }
    .grand .lbl { font-size:14px; font-weight:bold; letter-spacing:.02em; }
    .grand .int { font-size:12px; opacity:.9; }
    .grand .ven { font-size:22px; font-weight:bold; }
    .foot { margin-top:20px; border-top:1px solid #d7dee5; padding-top:8px; font-size:10px; color:#9aa6b1; text-align:center; }
    .company { font-size:11px; color:#5a6b7b; margin:-4px 0 12px; }
    .company .cl { font-weight:bold; color:#1a3a5c; }
    .conditions { margin-top:16px; border:1px solid #d7dee5; border-radius:8px; padding:10px 14px; background:#f8f5ee; page-break-inside:avoid; }
    .conditions h3 { margin:0 0 4px; font-size:12px; color:#C75B28; }
    .conditions p { margin:0; font-size:11px; color:#44525f; line-height:1.5; }
    @media print { .noprint { display:none; } }
    .noprint { position:fixed; top:10px; right:10px; }
    .noprint button { background:#1a3a5c; color:#fff; border:0; border-radius:6px; padding:8px 14px; font-size:13px; cursor:pointer; font-family:sans-serif; }
  </style></head>
  <body>
    <div class="noprint"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
    <div class="topbar">
      <img class="logo" src="${logoSrc}" onerror="this.style.display='none'"/>
      <span class="doc">Presupuesto</span>
    </div>
    ${companyBlock}
    ${coverTag}
    <h1>${esc(project.name)}</h1>
    ${project.location ? `<p class="loc">${esc(project.location)}</p>` : ""}
    <p class="date">Emitido el ${esc(today)}</p>
    ${levelBlocks || '<p class="loc">Aún no hay actividades en el modelo.</p>'}
    <div class="summary">
      <h2>Totales por tipo de actividad</h2>
      <table class="types">
        <tr><td><strong>Tipo</strong></td><td class="eur"><strong>Compra int.</strong></td><td class="eur"><strong>Venta</strong></td></tr>
        ${typeRows}
      </table>
    </div>
    <div class="grand">
      <span class="lbl">Total presupuesto</span>
      <span class="int">Compra interna: ${formatEuro(grand_total.subtotal_interna)}</span>
      <span class="ven">${formatEuro(grand_total.subtotal_venta)}</span>
    </div>
    ${company.footer ? `<div class="conditions"><h3>Condiciones</h3><p>${esc(company.footer).replace(/\n/g, "<br/>")}</p></div>` : ""}
    <div class="foot">Mori · Presupuesto generado para ${esc(project.name)} · ${esc(today)}</div>
    <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},500);};<\/script>
  </body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
}
